import { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import BottomSheet from './BottomSheet';
import { copyToClipboard } from './clipboard';

function downloadFilename(householdName, code) {
  const slug = (householdName || 'household')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'household';
  return `${slug}-invite-${code}.png`;
}

export default function QRCodeSheet({ code, inviteUrl, householdName, onClose }) {
  const svgRef = useRef(null);
  const canvasRef = useRef(null);
  const objectUrlRef = useRef(null);
  const [copied, setCopied] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState('');

  const canNativeShare = Boolean(navigator.share);
  const filename = useMemo(() => downloadFilename(householdName, code), [householdName, code]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  useEffect(() => {
    if (!canNativeShare && code && inviteUrl) {
      generatePngUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canNativeShare, code, inviteUrl]);

  async function copyText(value, key) {
    if (await copyToClipboard(value)) {
      setCopied(key);
      window.setTimeout(() => setCopied(current => current === key ? null : current), 1500);
    } else {
      setImageError('Copy failed. Select the code and copy it manually.');
    }
  }

  function svgToPngBlob() {
    return new Promise((resolve, reject) => {
      const svg = svgRef.current;
      const canvas = canvasRef.current;
      if (!svg || !canvas) {
        reject(new Error('QR image is not ready'));
        return;
      }

      const xml = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const size = 720;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        URL.revokeObjectURL(svgUrl);
        canvas.toBlob(blob => {
          if (blob) resolve(blob);
          else reject(new Error('Could not create QR image'));
        }, 'image/png');
      };
      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        reject(new Error('Could not render QR image'));
      };
      img.src = svgUrl;
    });
  }

  async function generatePngUrl() {
    setImageBusy(true);
    setImageError('');
    try {
      const blob = await svgToPngBlob();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setDownloadUrl(url);
      return { blob, url };
    } catch (err) {
      setImageError(err.message);
      return null;
    } finally {
      setImageBusy(false);
    }
  }

  async function shareImage() {
    const result = await generatePngUrl();
    if (!result) return;
    const file = new File([result.blob], filename, { type: 'image/png' });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          title: `Join ${householdName}`,
          text: `Use invite code ${code} to join ${householdName} on Aroidex.`,
          files: [file],
        });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }
    setImageError('Sharing is not available on this device. Use the download link instead.');
  }

  return (
    <BottomSheet
      open={Boolean(code && inviteUrl)}
      onClose={onClose}
      title={`Join ${householdName || 'household'}`}
      maxHeight="88dvh"
    >
      <div className="qr-sheet">
        <div className="qr-sheet-code-wrap">
          <QRCodeSVG
            ref={svgRef}
            value={inviteUrl}
            size={200}
            includeMargin
            className="qr-sheet-code"
          />
        </div>

        <button
          type="button"
          className="qr-sheet-raw-code"
          onClick={() => copyText(code, 'raw')}
          title="Copy invite code"
        >
          {copied === 'raw' ? 'Copied!' : code}
        </button>

        <div className="qr-sheet-actions">
          <button type="button" className="settings-lookup-action" onClick={() => copyText(code, 'code')}>
            {copied === 'code' ? 'Copied!' : 'Copy code'}
          </button>
          <button type="button" className="settings-lookup-action" onClick={() => copyText(inviteUrl, 'link')}>
            {copied === 'link' ? 'Copied!' : 'Copy link'}
          </button>
          {canNativeShare ? (
            <button type="button" className="settings-lookup-action" onClick={shareImage} disabled={imageBusy}>
              {imageBusy ? 'Preparing...' : 'Share as image'}
            </button>
          ) : (
            <a
              className={`settings-lookup-action qr-sheet-download ${!downloadUrl ? 'qr-sheet-download--disabled' : ''}`}
              href={downloadUrl || undefined}
              download={filename}
              aria-disabled={!downloadUrl}
              onClick={e => { if (!downloadUrl) e.preventDefault(); }}
            >
              {imageBusy ? 'Preparing...' : 'Download image'}
            </a>
          )}
        </div>

        {downloadUrl && canNativeShare && (
          <a className="qr-sheet-fallback-link" href={downloadUrl} download={filename}>
            Download image
          </a>
        )}
        {imageError && <p className="settings-save-error qr-sheet-error">{imageError}</p>}
        <canvas ref={canvasRef} className="qr-sheet-canvas" aria-hidden="true" />
      </div>
    </BottomSheet>
  );
}
